import { useMutation, useQuery } from "@apollo/client";
import { useCallback, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import Banner from "@/components/Banner";
import Breadcrumb from "@/components/Breadcrumb";
import Button from "@/components/Button";
import EmptyState from "@/components/EmptyState";
import PageHeading from "@/components/PageHeading/PageHeading";
import TextField from "@/components/TextField";
import { TableHeading } from "@/components/Table";
import { DOMAIN, PERMISSION, useHasCapabilityBool } from "@/hooks/useCapabilities";
import usePageTitle from "@/hooks/usePageTitle";
import pluralize from "@/lib/pluralize";
import { showErrorSnackbar, showSnackbar } from "@/lib/snackbarUtils";
import AppLayout from "@/layouts/AppLayout";
import { useNavigate } from "@/router";
import { useMembershipServicesNavLinks } from "../../../_membershipServicesNavLinks";
import { MEMBER_ELECTIONS_PAST_PATH, MEMBER_ELECTIONS_PAST_PRESIDENTS_PATH } from "../constants";
import {
  ADD_ELECTION_PAST_PRESIDENT_ASSIGNEE_MUTATION,
  GENERATE_ELECTION_PAST_PRESIDENTS_ROSTER_MUTATION,
  REMOVE_ELECTION_PAST_PRESIDENT_ASSIGNEE_MUTATION
} from "../mutations";
import { ELECTION_PAST_PRESIDENT_ASSIGNEES_QUERY } from "../queries";
import PastPresidentAssigneesTable from "./_components/PastPresidentAssigneesTable";
import { formatPastPresidentsElectionSummary } from "./_utils/formatPastPresidentsElectionSummary";
import {
  parsePastPresidentsRosterSearch,
  type PastPresidentsRosterNavigationState
} from "./_utils/pastPresidentsRosterNavigation";

export default function PastPresidentAssigneesPage() {
  usePageTitle(MEMBER_ELECTIONS_PAST_PRESIDENTS_PATH, "People | Past Presidents Roster");
  const navigate = useNavigate();
  const location = useLocation();
  const navLinks = useMembershipServicesNavLinks();
  const downloadContext = useMemo(() => parsePastPresidentsRosterSearch(location.search), [location.search]);
  const navigationState = location.state as PastPresidentsRosterNavigationState | null;
  const electionRuns = navigationState?.electionRuns ?? [];
  const hasDownloadContext = Boolean(downloadContext.electionRunId || downloadContext.electionRunIds?.length);

  const canManageApplications = useHasCapabilityBool({
    domain: DOMAIN.PEOPLE,
    permission: PERMISSION.manageApplications
  });

  const [newName, setNewName] = useState("");
  const [busyAction, setBusyAction] = useState<"add" | "remove" | "download" | null>(null);

  const { data, loading, error, refetch } = useQuery(ELECTION_PAST_PRESIDENT_ASSIGNEES_QUERY, {
    skip: !canManageApplications,
    fetchPolicy: "network-only"
  });

  const assignees = data?.electionPastPresidentAssignees ?? [];
  const assigneeIds = useMemo(
    () => data?.electionPastPresidentAssignees?.map((row) => row.id) ?? [],
    [data?.electionPastPresidentAssignees]
  );
  const isBusy = busyAction != null || loading;

  const [addAssignee] = useMutation(ADD_ELECTION_PAST_PRESIDENT_ASSIGNEE_MUTATION);
  const [removeAssignee] = useMutation(REMOVE_ELECTION_PAST_PRESIDENT_ASSIGNEE_MUTATION);
  const [generateRoster] = useMutation(GENERATE_ELECTION_PAST_PRESIDENTS_ROSTER_MUTATION);

  const runCount = downloadContext.electionRunIds?.length ?? (downloadContext.electionRunId ? 1 : 0);

  const defaultBody =
    "Add and edit Past Presidents used when splitting newly elected members across assignees for Past Presidents election roster exports.";
  const downloadBody =
    runCount > 0
      ? `This export splits newly elected members across Past Presidents for ${runCount} completed ${pluralize(runCount, "election run")}. Add or remove names below, then download.`
      : defaultBody;

  const electionSummary = electionRuns.length > 0 ? formatPastPresidentsElectionSummary(electionRuns) : undefined;

  const handleAdd = useCallback(async () => {
    const name = newName.trim();
    if (!name || busyAction) return;

    setBusyAction("add");
    try {
      const { data: mutationData } = await addAssignee({ variables: { input: { name } } });
      const result = mutationData?.addElectionPastPresidentAssignee;
      if (!result?.success || !result.assignee) {
        showErrorSnackbar("Could not add Past President", { subtitle: result?.message || "Please try again." });
        return;
      }
      setNewName("");
      await refetch();
    } catch {
      showErrorSnackbar("Could not add Past President", { subtitle: "Please try again." });
    } finally {
      setBusyAction(null);
    }
  }, [addAssignee, busyAction, newName, refetch]);

  const handleRemove = useCallback(
    async (id: string) => {
      if (busyAction) return;

      setBusyAction("remove");
      try {
        const { data: mutationData } = await removeAssignee({ variables: { input: { id } } });
        const result = mutationData?.removeElectionPastPresidentAssignee;
        if (!result?.success) {
          showErrorSnackbar("Could not remove Past President", { subtitle: result?.message || "Please try again." });
          return;
        }
        await refetch();
      } catch {
        showErrorSnackbar("Could not remove Past President", { subtitle: "Please try again." });
      } finally {
        setBusyAction(null);
      }
    },
    [busyAction, refetch, removeAssignee]
  );

  const handleDownload = useCallback(async () => {
    if (busyAction || assigneeIds.length === 0 || !hasDownloadContext) return;

    setBusyAction("download");
    try {
      const input =
        downloadContext.electionRunIds && downloadContext.electionRunIds.length > 0
          ? {
              electionRunIds: downloadContext.electionRunIds,
              pastPresidentAssigneeIds: assigneeIds
            }
          : {
              electionRunId: downloadContext.electionRunId,
              pastPresidentAssigneeIds: assigneeIds
            };

      const { data: mutationData } = await generateRoster({ variables: { input } });
      const result = mutationData?.generateElectionPastPresidentsRoster;

      if (!result?.success || !result.url) {
        showErrorSnackbar("Past Presidents roster not generated", {
          subtitle: result?.message || "Please try again later."
        });
        return;
      }

      showSnackbar(result.message || "Export generated");
      window.location.assign(result.url);
    } catch {
      showErrorSnackbar("Past Presidents roster not generated", { subtitle: "Please try again later." });
    } finally {
      setBusyAction(null);
    }
  }, [assigneeIds, busyAction, downloadContext, generateRoster, hasDownloadContext]);

  const backLink = hasDownloadContext
    ? ({ to: MEMBER_ELECTIONS_PAST_PATH } as const)
    : ({ to: "/people/membership-services/member-elections" } as const);

  return (
    <AppLayout interior bottomBar navLinks={navLinks}>
      <Breadcrumb title="Member Elections" compactTitle="Past Presidents Roster" backLink={backLink} />
      <PageHeading title="Past Presidents Roster" compact />
      <div className="w-full">
        <TableHeading
          heading="Past Presidents Roster"
          body={hasDownloadContext ? downloadBody : defaultBody}
          actions={
            canManageApplications && hasDownloadContext
              ? [
                  {
                    label: "Download roster",
                    icon: "download",
                    variant: "filled",
                    onClick: handleDownload,
                    disabled: isBusy || assignees.length === 0,
                    testId: "past-presidents-roster-download"
                  }
                ]
              : []
          }
        />

        {!canManageApplications ? (
          <EmptyState
            iconName="lock"
            title="You Don't Have Access to This Page"
            caption="Managing Past Presidents roster assignees requires the manage applications permission. Contact your administrator if you need access."
          />
        ) : error ? (
          <EmptyState iconName="error" title="Error" caption="An error occurred while loading Past Presidents." />
        ) : (
          <div className="flex flex-col gap-6">
            {hasDownloadContext && electionSummary ? (
              <Banner variant="info" description={electionSummary} hideSecondaryDismiss />
            ) : null}
            <div className="flex flex-col gap-4 sm:flex-row sm:items-end">
              <TextField
                className="flex-1"
                label="Add Past President"
                value={newName}
                onChangeText={setNewName}
                disabled={isBusy}
              />
              <Button
                variant="outlined"
                onClick={handleAdd}
                disabled={isBusy || !newName.trim()}
                testId="past-presidents-add"
              >
                Add
              </Button>
            </div>
            <PastPresidentAssigneesTable
              loading={loading}
              assignees={assignees}
              busy={isBusy}
              onRemove={handleRemove}
            />
            {hasDownloadContext ? (
              <div className="flex justify-end">
                <Button
                  variant="text"
                  onClick={() => navigate(MEMBER_ELECTIONS_PAST_PATH)}
                  disabled={isBusy}
                  testId="past-presidents-roster-cancel"
                >
                  Back to past elections
                </Button>
              </div>
            ) : null}
          </div>
        )}
      </div>
    </AppLayout>
  );
}
